import {
  ICompoundMeasureFieldsFragment,
  IMeasureFieldsFragment,
  ISimpleMeasureFieldsFragment,
} from '../../../apollo/graphqlSchema';
import { IAnalyticsMeasure } from '../../types';
import { UnreachableCaseError } from 'ts-essentials';
import {
  getEventNamesFromEventConditionCollection,
  getFieldNamesFromEventConditionCollection,
  uniqueAnalyticsLoggedEventFunction,
} from '../event/utils';
import { uniqBy } from 'lodash';
import { uniqueAnalyticsLoggedFieldFunction } from '../field/utils';

const getSimpleMeasureFunction = (
  measure: ISimpleMeasureFieldsFragment,
): IAnalyticsMeasure => ({
  function: {
    name: measure.function.name,
  },
});

const getCompoundMeasureFunction = (
  measure: ICompoundMeasureFieldsFragment,
): IAnalyticsMeasure[] => measure.measures.map(getSimpleMeasureFunction);

export const getMeasures = (
  measure: IMeasureFieldsFragment,
): IAnalyticsMeasure[] => {
  if (measure.__typename === 'SimpleMeasure') {
    return [getSimpleMeasureFunction(measure)];
  } else if (measure.__typename === 'CompoundMeasure') {
    return getCompoundMeasureFunction(measure);
  } else {
    throw new UnreachableCaseError(measure);
  }
};

const getEventsFromSimpleMeasure = (measure: ISimpleMeasureFieldsFragment) =>
  getEventNamesFromEventConditionCollection(measure.eventConditions);

export const getEventNamesFromMeasure = (measure: IMeasureFieldsFragment) => {
  if (measure.__typename === 'SimpleMeasure') {
    return getEventsFromSimpleMeasure(measure);
  } else if (measure.__typename === 'CompoundMeasure') {
    return measure.measures.flatMap(getEventsFromSimpleMeasure);
  } else {
    throw new UnreachableCaseError(measure);
  }
};

const getFieldNamesFromSimpleMeasure = (
  measure: ISimpleMeasureFieldsFragment,
) => getFieldNamesFromEventConditionCollection(measure.eventConditions);

export const getFieldNamesFromMeasure = (measure: IMeasureFieldsFragment) => {
  if (measure.__typename === 'SimpleMeasure') {
    return getFieldNamesFromSimpleMeasure(measure);
  } else if (measure.__typename === 'CompoundMeasure') {
    return measure.measures.flatMap(getFieldNamesFromSimpleMeasure);
  } else {
    throw new UnreachableCaseError(measure);
  }
};

export const getUniqEventNamesFromMeasure = (measure: IMeasureFieldsFragment) =>
  uniqBy(getEventNamesFromMeasure(measure), uniqueAnalyticsLoggedEventFunction);

export const getUniqFieldNamesFromMeasure = (measure: IMeasureFieldsFragment) =>
  uniqBy(getFieldNamesFromMeasure(measure), uniqueAnalyticsLoggedFieldFunction);
